import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface SelectOption {
  value: string;
  label: string;
  shortLabel?: string;
  icon?: React.ReactNode;
  description?: string;
}

interface SelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  className?: string;
  searchable?: boolean;
  disabled?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  error,
  icon: TriggerIcon,
  className,
  searchable = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  
  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handlePointerOutside = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('pointerdown', handlePointerOutside);
    }
    return () => document.removeEventListener('pointerdown', handlePointerOutside);
  }, [isOpen]);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={cn("relative w-full space-y-1.5", className)} ref={containerRef}>
      {label && (
        <label className="text-[10px] uppercase tracking-widest font-bold text-gray-400 ml-1 block truncate">
          {label}
        </label>
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full min-h-[50px] flex items-center justify-between px-4 py-3.5 bg-white/5 border border-white/10 rounded-xl transition-all duration-300 text-left group touch-manipulation",
          isOpen ? "border-brand-primary/50 ring-4 ring-brand-primary/10 bg-white/10" : "hover:bg-white/8 hover:border-white/20",
          error ? "border-red-500/50" : "",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          {TriggerIcon && <div className="text-gray-400 group-hover:text-brand-primary transition-colors shrink-0">{TriggerIcon}</div>}
          <span className={cn(
            "font-bold truncate text-sm",
            selectedOption ? "text-white" : "text-gray-500"
          )}>
            {selectedOption ? (selectedOption.shortLabel || selectedOption.label) : placeholder}
          </span>
        </div>
        <ChevronDown 
          size={18} 
          className={cn(
            "text-gray-500 transition-transform duration-300 shrink-0 ml-2",
            isOpen && "rotate-180 text-brand-primary"
          )} 
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="absolute top-full left-0 right-0 min-w-full sm:min-w-[260px] mt-2 bg-[#0A0A0A]/95 backdrop-blur-2xl border border-white/10 rounded-xl shadow-2xl z-[100] overflow-hidden"
          >
            {searchable && (
              <div className="p-3 border-b border-white/5 space-y-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Search options..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full min-h-11 bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-sm text-white outline-none focus:border-brand-primary/50 transition-colors"
                  />
                  {searchQuery && (
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 min-h-10 min-w-10 text-gray-500 hover:text-white"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-2 space-y-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "w-full min-h-12 flex items-center justify-between p-3 rounded-xl transition-all text-left group relative touch-manipulation",
                      value === option.value ? "bg-brand-primary/10 text-brand-primary" : "hover:bg-white/5 text-gray-400 hover:text-white"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {option.icon && (
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                          value === option.value ? "bg-brand-primary/20" : "bg-white/5 group-hover:bg-white/10"
                        )}>
                          {option.icon}
                        </div>
                      )}
                      <div>
                        <span className="font-bold block text-sm">{option.label}</span>
                        {option.description && (
                          <span className="text-[10px] opacity-50 block uppercase tracking-wider mt-0.5 font-medium">
                            {option.description}
                          </span>
                        )}
                      </div>
                    </div>
                    {value === option.value && <Check size={16} strokeWidth={3} />}
                  </button>
                ))
              ) : (
                <div className="py-8 text-center text-gray-500 text-sm">
                  No results found
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <p className="text-[10px] text-red-500 font-bold uppercase tracking-wider ml-1">
          {error}
        </p>
      )}
    </div>
  );
};
